import React from 'react';
import { useTheme } from '../hooks/useTheme';
import { 
  MdTextFields, 
  MdSell, 
  MdPalette, 
  MdBrush, 
  MdStyle,
  MdOutlineSmartButton,
  MdFormatShapes
} from 'react-icons/md';

/**
 * ThemeStyleGuide component
 * 
 * A comprehensive guide showing all theme-related utility classes
 * available in the application for developers.
 */
const ThemeStyleGuide: React.FC = () => {
  const { colorTheme } = useTheme();

  return (
    <div className="p-6 dark:bg-dark-surface bg-white rounded-lg shadow-lg border dark:border-gray-700 border-gray-200 mt-8">
      <div className="flex items-center gap-3 mb-6">
        <MdStyle className="w-6 h-6 text-theme" />
        <h2 className="text-xl font-bold dark:text-white text-gray-900">Theme Style Guide</h2>
      </div>
      
      <div className="text-left dark:text-gray-300 text-gray-700 mb-6">
        <p>Use these utility classes to apply the current theme color to your components.</p>
        <p className="text-sm dark:text-gray-400 text-gray-500 mt-2">Current theme: <span className="text-theme font-medium capitalize">{colorTheme}</span></p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Text colors */}
        <div className="p-4 border dark:border-gray-700 border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <MdTextFields className="text-theme" />
            <h3 className="font-medium">Text Colors</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">.text-theme</code>
              <span className="text-theme mt-1">This text uses the accent color</span>
            </div>
          </div>
        </div>
        
        {/* Background colors */}
        <div className="p-4 border dark:border-gray-700 border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <MdPalette className="text-theme" />
            <h3 className="font-medium">Background Colors</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">.bg-theme</code>
              <div className="bg-theme text-white p-2 rounded mt-1">Full background color</div>
            </div>
            
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">.bg-theme-10</code>
              <div className="bg-theme-10 dark:text-white text-gray-900 p-2 rounded mt-1">10% opacity background</div>
            </div>
            
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">.bg-theme-20</code>
              <div className="bg-theme-20 dark:text-white text-gray-900 p-2 rounded mt-1">20% opacity background</div>
            </div>
          </div>
        </div>
        
        {/* Borders */}
        <div className="p-4 border dark:border-gray-700 border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <MdSell className="text-theme" />
            <h3 className="font-medium">Borders</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">.border-theme</code>
              <div className="border-2 border-theme p-2 rounded mt-1">Element with themed border</div>
            </div>
            
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">.border-l-4.border-theme</code>
              <div className="border-l-4 border-theme pl-2 mt-1">Left accent border</div>
            </div>
          </div>
        </div>
        
        {/* Effects */}
        <div className="p-4 border dark:border-gray-700 border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <MdBrush className="text-theme" />
            <h3 className="font-medium">Shadow Effects</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">.shadow-theme</code>
              <div className="shadow-theme p-2 rounded mt-1">Themed box shadow</div>
            </div>
            
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">.glow-theme</code>
              <div className="glow-theme p-2 rounded mt-1">Themed glow effect</div>
            </div>
            
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">.ring-theme</code>
              <div className="ring-theme p-2 rounded mt-1">Themed ring/outline</div>
            </div>
          </div>
        </div>
        
        {/* Buttons */}
        <div className="p-4 border dark:border-gray-700 border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <MdOutlineSmartButton className="text-theme" />
            <h3 className="font-medium">Button Styles</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">.bg-theme.text-white</code>
              <button className="bg-theme text-white px-3 py-2 rounded mt-1">Primary Button</button>
            </div>
            
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">.border-theme.text-theme</code>
              <button className="border-2 border-theme text-theme px-3 py-2 rounded mt-1">Outline Button</button>
            </div>
            
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">.bg-theme-10.text-theme</code>
              <button className="bg-theme-10 text-theme px-3 py-2 rounded mt-1">Light Button</button>
            </div>
          </div>
        </div>
        
        {/* Gradients */}
        <div className="p-4 border dark:border-gray-700 border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <MdFormatShapes className="text-theme" />
            <h3 className="font-medium">Gradients (Tailwind)</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">from-theme.to-transparent</code>
              <div className="bg-gradient-to-r from-theme to-transparent p-3 rounded text-white mt-1">
                Horizontal gradient
              </div>
            </div>
            
            <div className="flex flex-col">
              <code className="text-xs dark:bg-gray-800 bg-gray-100 p-1 rounded">from-theme.to-neon-purple</code>
              <div className="bg-gradient-to-br from-theme to-neon-purple p-3 rounded text-white mt-1">
                Diagonal gradient
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-6 text-sm dark:text-gray-400 text-gray-600">
        <p>Note: All theme-related classes adapt to the user's selected theme color.</p>
      </div>
    </div>
  );
};

export default ThemeStyleGuide;
